/**
 * Erros da API no formato RFC 9457 (problem+json) com os membros do Hive:
 * `code`, `why`, `fix` e, para campos, `fields: {campo: mensagem}`.
 */
export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  why?: string;
  fix?: string;
  ref?: string;
  fields?: Record<string, string>;
  /** Corpo completo, para membros extras de um erro específico (ex.: `usedIn` do grupo em uso). */
  raw?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    super(problem.title || problem.detail || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  get code(): string {
    // Sem corpo do núcleo e com 0/502/503/504: o servidor não respondeu (reiniciando, rede caiu).
    if (!this.problem.code && [0, 502, 503, 504].includes(this.status)) return 'network';
    return this.problem.code ?? `http-${this.status}`;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Lê um corpo qualquer como Problem (aceita também respostas que não são problem+json). */
export function toProblem(status: number, body: unknown): Problem {
  if (isRecord(body)) {
    const fields = isRecord(body.fields)
      ? Object.fromEntries(Object.entries(body.fields).map(([k, v]) => [k, String(v)]))
      : undefined;
    const str = (k: string) => (typeof body[k] === 'string' ? (body[k] as string) : undefined);
    return {
      type: str('type'),
      title: str('title'),
      status: typeof body.status === 'number' ? body.status : status,
      detail: str('detail'),
      instance: str('instance'),
      code: str('code'),
      why: str('why'),
      fix: str('fix'),
      ref: str('ref'),
      fields,
      raw: body,
    };
  }
  return { status, title: typeof body === 'string' && body ? body.slice(0, 300) : undefined };
}

/** Erro de rede (servidor fora do ar, CORS, conexão caiu). */
export function networkError(cause: unknown): ApiError {
  const e = new ApiError(0, { code: 'network', status: 0 });
  (e as { cause?: unknown }).cause = cause;
  return e;
}
